import { Security, Warning, Fingerprint } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  _IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getProfile, getOrganization } from 'client/modules/Auth/AuthReducer';
import {
  clearTwoFactorError,
  disable2FA,
  get2FAStatus,
  regenerateBackupCodes,
} from 'client/modules/Auth/TwoFactorActions';
import {
  getTwoFactorBackupCodes,
  getTwoFactorError,
  getTwoFactorLoading,
  getTwoFactorStatus,
} from 'client/modules/Auth/TwoFactorReducer';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { authenticatePasskey } from '../../../Auth/PasskeyActions';
import TwoFactorSetup from '../../../Auth/components/TwoFactorSetup/TwoFactorSetup';

const messages = defineMessages({
  title: {
    id: 'TwoFactor.Title',
    defaultMessage: 'Two-Factor Authentication',
  },
  description: {
    id: 'TwoFactor.Description',
    defaultMessage:
      'Add an extra layer of security to your account with two-factor authentication.',
  },
  enabled: {
    id: 'TwoFactor.Enabled',
    defaultMessage: 'Enabled',
  },
  disabled: {
    id: 'TwoFactor.Disabled',
    defaultMessage: 'Disabled',
  },
  setup: {
    id: 'TwoFactor.Setup',
    defaultMessage: 'Set up 2FA',
  },
  disable: {
    id: 'TwoFactor.Disable',
    defaultMessage: 'Disable 2FA',
  },
  setupTitle: {
    id: 'TwoFactor.SetupTitle',
    defaultMessage: 'Set up Two-Factor Authentication',
  },
  step1: {
    id: 'TwoFactor.Step1',
    defaultMessage: 'Step 1: Install an authenticator app',
  },
  step1Description: {
    id: 'TwoFactor.Step1Description',
    defaultMessage:
      'Download and install an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.',
  },
  step2: {
    id: 'TwoFactor.Step2',
    defaultMessage: 'Step 2: Scan the QR code',
  },
  step2Description: {
    id: 'TwoFactor.Step2Description',
    defaultMessage: 'Open your authenticator app and scan this QR code to add your account.',
  },
  step3: {
    id: 'TwoFactor.Step3',
    defaultMessage: 'Step 3: Enter verification code',
  },
  step3Description: {
    id: 'TwoFactor.Step3Description',
    defaultMessage: 'Enter the 6-digit code from your authenticator app to verify the setup.',
  },
  verificationCode: {
    id: 'TwoFactor.VerificationCode',
    defaultMessage: 'Verification code',
  },
  backupCodesDescription: {
    id: 'TwoFactor.BackupCodesDescription',
    defaultMessage:
      'Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.',
  },
  regenerateBackupCodes: {
    id: 'TwoFactor.RegenerateBackupCodes',
    defaultMessage: 'Regenerate backup codes',
  },
  copyBackupCodes: {
    id: 'TwoFactor.CopyBackupCodes',
    defaultMessage: 'Copy backup codes',
  },
  downloadBackupCodes: {
    id: 'TwoFactor.DownloadBackupCodes',
    defaultMessage: 'Download backup codes',
  },
  disableTitle: {
    id: 'TwoFactor.DisableTitle',
    defaultMessage: 'Disable Two-Factor Authentication',
  },
  disableDescription: {
    id: 'TwoFactor.DisableDescription',
    defaultMessage:
      'This will remove the extra security layer from your account. You can enable it again at any time.',
  },
  currentPassword: {
    id: 'TwoFactor.CurrentPassword',
    defaultMessage: 'Current password',
  },
  totpOrBackupCode: {
    id: 'TwoFactor.TotpOrBackupCode',
    defaultMessage: 'TOTP code or backup code',
  },
  cancel: {
    id: 'Common.Cancel',
    defaultMessage: 'Cancel',
  },
  enable: {
    id: 'TwoFactor.Enable',
    defaultMessage: 'Enable',
  },
  secretKey: {
    id: 'TwoFactor.SecretKey',
    defaultMessage: 'Secret key',
  },
  copySecret: {
    id: 'TwoFactor.CopySecret',
    defaultMessage: 'Copy secret key',
  },
  manualEntry: {
    id: 'TwoFactor.ManualEntry',
    defaultMessage: "Can't scan the QR code? Enter this secret key manually:",
  },
  enabledAt: {
    id: 'TwoFactor.EnabledAt',
    defaultMessage: 'Enabled on {date}',
  },
  next: {
    id: 'Common.Next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'Common.Back',
    defaultMessage: 'Back',
  },
  finish: {
    id: 'Common.Finish',
    defaultMessage: 'Finish',
  },
  success: {
    id: 'TwoFactor.Success',
    defaultMessage: 'Success!',
  },
  setupCompleteTitle: {
    id: 'TwoFactor.SetupCompleteTitle',
    defaultMessage: 'Two-Factor Authentication Enabled',
  },
  cantScanQRCode: {
    id: 'TwoFactor.CantScanQRCode',
    defaultMessage: "Can't scan the QR code?",
  },
  enforcedWarning: {
    id: 'TwoFactor.EnforcedWarning',
    defaultMessage:
      'Two-factor authentication is enforced by your organization. You cannot disable it.',
  },
  enforcedDisableWarning: {
    id: 'TwoFactor.EnforcedDisableWarning',
    defaultMessage:
      'Two-factor authentication is enforced by your organization and cannot be disabled.',
  },
});

function TwoFactorSettings() {
  const { formatMessage, formatDate } = useIntl();
  const dispatch = useDispatch();

  const profile = useSelector(getProfile);
  const organization = useSelector(getOrganization);
  const status = useSelector(getTwoFactorStatus);
  const backupCodes = useSelector(getTwoFactorBackupCodes);
  const error = useSelector(getTwoFactorError);
  const isLoading = useSelector(getTwoFactorLoading);

  const [disableOpen, setDisableOpen] = useState(false);
  const [backupCodesOpen, setBackupCodesOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableToken, setDisableToken] = useState('');
  const [regenerateToken, setRegenerateToken] = useState('');

  useEffect(() => {
    dispatch(get2FAStatus());
  }, [dispatch]);

  const is2FAEnforced = organization?.twoFactor?.enforced || false;

  const handleSetupOpen = useCallback(() => {
    setSetupOpen(true);
  }, []);

  const handleSetupClose = useCallback(() => {
    setSetupOpen(false);
    dispatch(clearTwoFactorError());
    // Refresh 2FA status after setup
    dispatch(get2FAStatus());
  }, [dispatch]);

  const handleSetupComplete = useCallback(() => {
    handleSetupClose();
  }, [handleSetupClose]);

  const handleDisableOpen = useCallback(() => {
    setDisableOpen(true);
    setDisableToken('');
  }, []);

  const handleDisableClose = useCallback(() => {
    setDisableOpen(false);
    setDisableToken('');
    dispatch(clearTwoFactorError());
  }, [dispatch]);

  const handleDisable = useCallback(async () => {
    try {
      await dispatch(disable2FA(disableToken));
      handleDisableClose();
      dispatch(get2FAStatus());
    } catch (_err) {
      // Error is handled by reducer
    }
  }, [dispatch, disableToken, handleDisableClose]);

  const handleDisableWithPasskey = useCallback(async () => {
    try {
      await dispatch(authenticatePasskey());
      // If passkey auth succeeds, we can disable 2FA
      await dispatch(disable2FA('passkey-verified'));
      handleDisableClose();
      dispatch(get2FAStatus());
    } catch (_err) {
      // Error is handled by reducer
    }
  }, [dispatch, handleDisableClose]);

  const handleRegenerateBackupCodes = useCallback(async () => {
    try {
      await dispatch(regenerateBackupCodes(regenerateToken));
      setBackupCodesOpen(true);
      setRegenerateToken('');
    } catch (_err) {
      // Error is handled by reducer
    }
  }, [dispatch, regenerateToken]);

  const handleRegenerateWithPasskey = useCallback(async () => {
    try {
      await dispatch(authenticatePasskey());
      // If passkey auth succeeds, we can regenerate backup codes
      await dispatch(regenerateBackupCodes('passkey-verified'));
      setBackupCodesOpen(true);
      setRegenerateToken('');
    } catch (_err) {
      // Error is handled by reducer
    }
  }, [dispatch]);

  if (!organization) {
    return <Skeleton variant="rounded" width="100%" height={200} />;
  }

  return (
    <>
      <Card elevation={1}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" component="h3">
              {formatMessage(messages.title)}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {formatMessage(messages.description)}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="body1">
              <FormattedMessage id="TwoFactor.Status" defaultMessage="Status:" />
            </Typography>
            <Chip
              icon={status.enabled ? <Security /> : <Warning />}
              label={formatMessage(status.enabled ? messages.enabled : messages.disabled)}
              color={status.enabled ? 'success' : 'warning'}
              variant={status.enabled ? 'contained' : 'outlined'}
              size="small"
              sx={{ color: status.enabled ? 'primary.contrastText' : 'text.warning', px: 0.5 }}
            />
          </Box>

          {status.enabled && status.enabledAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {formatMessage(messages.enabledAt, {
                date: formatDate(new Date(status.enabledAt), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              })}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {!status.enabled ? (
              <Button variant="contained" onClick={handleSetupOpen} disabled={isLoading}>
                {formatMessage(messages.setup)}
              </Button>
            ) : (
              <>
                <Tooltip title={is2FAEnforced ? formatMessage(messages.enforcedWarning) : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDisableOpen}
                      disabled={isLoading || (is2FAEnforced && !profile.hasPasskeys)}
                    >
                      {formatMessage(messages.disable)}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  onClick={() => setBackupCodesOpen(true)}
                  disabled={isLoading}
                >
                  {formatMessage(messages.regenerateBackupCodes)}
                </Button>
              </>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Two Factor Setup Dialog */}
      <TwoFactorSetup
        open={setupOpen}
        onClose={handleSetupClose}
        onComplete={handleSetupComplete}
        enforced={is2FAEnforced}
      />

      {/* Disable Dialog */}
      <Dialog open={disableOpen} onClose={handleDisableClose} maxWidth="sm" fullWidth>
        <DialogTitle>{formatMessage(messages.disableTitle)}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {formatMessage(messages.disableDescription)}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <FormattedMessage
              id="TwoFactor.DisableVerification"
              defaultMessage="To disable two-factor authentication, verify your identity with your authenticator app or a backup code."
            />
          </Alert>

          <TextField
            label={formatMessage(messages.totpOrBackupCode)}
            value={disableToken}
            onChange={e => setDisableToken(e.target.value)}
            fullWidth
            helperText={error}
            error={!!error}
            placeholder="123456 or ABCD1234"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisableClose}>{formatMessage(messages.cancel)}</Button>
          <Button
            onClick={handleDisableWithPasskey}
            variant="outlined"
            color="primary"
            disabled={isLoading || (is2FAEnforced && !profile.hasPasskeys)}
            startIcon={<Fingerprint />}
          >
            <FormattedMessage id="TwoFactor.UsePasskey" defaultMessage="Use Passkey" />
          </Button>
          <Button
            onClick={handleDisable}
            variant="contained"
            color="error"
            disabled={!disableToken || isLoading || (is2FAEnforced && !profile.hasPasskeys)}
          >
            {isLoading ? <CircularProgress size={20} /> : formatMessage(messages.disable)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog
        open={backupCodesOpen}
        onClose={() => setBackupCodesOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{formatMessage(messages.regenerateBackupCodes)}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <FormattedMessage
              id="TwoFactor.RegenerateVerification"
              defaultMessage="To regenerate backup codes, verify your identity with your authenticator app or a backup code."
            />
          </Alert>
          <TextField
            label={formatMessage(messages.totpOrBackupCode)}
            value={regenerateToken}
            onChange={e => setRegenerateToken(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="123456 or ABCD1234"
          />

          {backupCodes.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {backupCodes.map((code, index) => (
                  <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {code}
                  </Typography>
                ))}
              </Box>
            </Paper>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupCodesOpen(false)}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            onClick={handleRegenerateWithPasskey}
            variant="outlined"
            color="primary"
            disabled={isLoading}
            startIcon={<Fingerprint />}
          >
            <FormattedMessage id="TwoFactor.UsePasskey" defaultMessage="Use Passkey" />
          </Button>
          <Button
            onClick={handleRegenerateBackupCodes}
            variant="contained"
            disabled={!regenerateToken || isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              formatMessage(messages.regenerateBackupCodes)
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TwoFactorSettings;
